/**
 * PURPOSE: Historial administrativo de Comunicados (borradores y publicados, incluidos ocultos).
 * RESPONSIBILITIES: Listar con paginación por cursor ("Cargar más"), mostrar estado/visibilidad/
 *   adjunto/contadores de push con la terminología aprobada, y permitir seleccionar un ítem para
 *   editar (borrador) o ver (publicado) en el panel de detalle. Usa AuditNotificationPanel.jsx sólo
 *   como precedente de UI/paginación — no comparte servicio, estado ni backend con auditoría.
 * COLLABORATORS: HayllisCommunicationsPanel.jsx, hayllisNotificationsService.js,
 *   hayllisCommunicationsDisplay.utils.js.
 * ROUTE: winscore/src/admin/HayllisCommunications/HayllisCommunicationsList.jsx
 */
import React from "react";
import { FiFile, FiImage, FiPlus } from "react-icons/fi";

import {
  buildPushSummary,
  formatDateTime,
  PUSH_STATUS_LABELS,
  STATUS_LABELS,
} from "./hayllisCommunicationsDisplay.utils.js";

const STATUS_FILTERS = [
  { value: "", label: "Todos" },
  { value: "DRAFT", label: "Borradores" },
  { value: "PUBLISHED", label: "Publicados" },
];

const STATUS_TONE = {
  DRAFT: "bg-slate-200 text-slate-700",
  PUBLISHED: "bg-emerald-100 text-emerald-700",
};

const HayllisCommunicationsList = ({
  items,
  loading,
  loadingMore,
  hasMore,
  loadError,
  statusFilter,
  onStatusFilterChange,
  onLoadMore,
  onSelect,
  onCreateNew,
  selectedId,
}) => {
  return (
    <section className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-white">Historial de comunicados</h2>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Anuncios enviados como notificación push a Hayllis Mobile.
          </p>
        </div>
        <button
          type="button"
          onClick={onCreateNew}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white hover:bg-blue-700"
        >
          <FiPlus /> Nuevo comunicado
        </button>
      </div>

      <div className="mb-4 flex gap-2">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => onStatusFilterChange(filter.value)}
            className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
              statusFilter === filter.value
                ? "bg-blue-600 text-white"
                : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200"
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-300">Cargando historial...</div>
      ) : loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-center text-sm font-semibold text-red-600">
          {loadError}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500 dark:border-slate-600 dark:text-slate-300">
          Todavía no hay comunicados{statusFilter ? " en este filtro" : ""}.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const isSelected = item.id === selectedId;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelect(item)}
                className={`block w-full rounded-lg border p-3 text-left transition ${
                  isSelected
                    ? "border-blue-400 bg-blue-50 dark:bg-blue-500/10"
                    : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-600 dark:bg-gray-800 dark:hover:bg-gray-700"
                } ${!item.visible ? "opacity-70" : ""}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${STATUS_TONE[item.status]}`}>
                    {STATUS_LABELS[item.status] || item.status}
                  </span>
                  {!item.visible && (
                    <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-bold text-slate-600">
                      Oculto
                    </span>
                  )}
                  {item.attachment && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 dark:text-slate-300">
                      {item.attachment.mime === "application/pdf" ? <FiFile /> : <FiImage />}
                      {item.attachment.name}
                    </span>
                  )}
                  {item.status === "PUBLISHED" && (
                    <span className="text-xs font-semibold text-slate-500 dark:text-slate-300">
                      {PUSH_STATUS_LABELS[item.pushStatus] || item.pushStatus}
                    </span>
                  )}
                </div>

                <h3 className="mt-1.5 truncate font-bold text-slate-800 dark:text-white">{item.title}</h3>

                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Creado: {formatDateTime(item.createdAt)} · {item.createdBy?.username || "—"}
                  {item.status === "PUBLISHED" && (
                    <>
                      {" "}
                      · Publicado: {formatDateTime(item.publishedAt)} · {item.publishedBy?.username || "—"}
                    </>
                  )}
                </p>

                {item.status === "PUBLISHED" && (
                  <p className="mt-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
                    {buildPushSummary(item)}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}

      {hasMore && !loading && (
        <button
          type="button"
          disabled={loadingMore}
          onClick={onLoadMore}
          className="mt-4 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-gray-800"
        >
          {loadingMore ? "Cargando..." : "Cargar más"}
        </button>
      )}
    </section>
  );
};

export default HayllisCommunicationsList;
